import type {
  MealCategoryId,
  MealEntry,
  MealFormValues,
  SupplementEntry,
  SupplementFormValues,
} from '../../types/mealTracking';
import {
  MEAL_CATEGORY_LABEL,
  SUPPLEMENT_FREQUENCY_LABEL,
  SUPPLEMENT_UNIT_LABEL,
} from './mealTracking.constants';

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export const toDateOnly = (value: string): string => value.trim().slice(0, 10);

export const getTodayDateOnly = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const shiftDateOnly = (isoDate: string, dayDelta: number): string => {
  const [year, month, day] = isoDate.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day + dayDelta));
  return date.toISOString().slice(0, 10);
};

export const formatMealLongDate = (isoDate: string): string => {
  if (!DATE_ONLY_PATTERN.test(isoDate)) {
    return isoDate;
  }

  const [year, month, day] = isoDate.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return new Intl.DateTimeFormat('pt-BR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
};

export const formatMealListDateLabel = (isoDate: string, todayIso = getTodayDateOnly()): string => {
  const formatted = formatMealLongDate(isoDate);
  if (isoDate === todayIso) {
    return `Hoje, ${formatted}`;
  }

  return formatted;
};

export const formatMealDisplayTime = (time: string): string => time.trim().slice(0, 5);

export const resolveMealTitle = (meal: Pick<MealEntry, 'categoryId' | 'customTitle'>): string => {
  if (meal.categoryId === 'other') {
    const custom = meal.customTitle?.trim();
    return custom || MEAL_CATEGORY_LABEL.other;
  }

  return MEAL_CATEGORY_LABEL[meal.categoryId];
};

export const sortMealsChronologically = (meals: MealEntry[]): MealEntry[] => (
  [...meals].sort((left, right) => {
    const timeCompare = left.time.localeCompare(right.time);
    if (timeCompare !== 0) {
      return timeCompare;
    }

    return left.createdAt.localeCompare(right.createdAt);
  })
);

export const mealToFormValues = (meal: MealEntry): MealFormValues => ({
  date: meal.date,
  time: meal.time,
  categoryId: meal.categoryId,
  customTitle: meal.customTitle ?? '',
  description: meal.description,
  photos: meal.photos.map((photo) => ({ ...photo })),
});

export const createEmptyMealForm = (date: string): MealFormValues => ({
  date,
  time: '',
  categoryId: 'breakfast',
  customTitle: '',
  description: '',
  photos: [],
});

export const validateMealForm = (values: MealFormValues): Partial<Record<keyof MealFormValues | 'customTitle', string>> => {
  const errors: Partial<Record<keyof MealFormValues | 'customTitle', string>> = {};

  if (!DATE_ONLY_PATTERN.test(values.date)) {
    errors.date = 'Informe uma data válida.';
  }

  if (!/^\d{2}:\d{2}$/.test(values.time.trim())) {
    errors.time = 'Informe o horário.';
  }

  if (!values.description.trim()) {
    errors.description = 'Descreva o que você comeu.';
  }

  if (values.categoryId === 'other' && !values.customTitle.trim()) {
    errors.customTitle = 'Informe o nome da refeição.';
  }

  return errors;
};

export const createMealId = (): string => `meal-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const createPhotoId = (): string => `photo-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const createSupplementId = (): string => (
  `supplement-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
);

export const createEmptySupplementForm = (date: string): SupplementFormValues => ({
  name: '',
  amount: '',
  unitId: 'g',
  customUnit: '',
  date,
  time: '',
  frequencyId: 'daily',
  customFrequency: '',
  brand: '',
  notes: '',
});

export const supplementToFormValues = (entry: SupplementEntry): SupplementFormValues => ({
  name: entry.name,
  amount: entry.amount,
  unitId: entry.unitId,
  customUnit: entry.customUnit ?? '',
  date: entry.date,
  time: entry.time,
  frequencyId: entry.frequencyId,
  customFrequency: entry.customFrequency ?? '',
  brand: entry.brand,
  notes: entry.notes,
});

export const validateSupplementForm = (
  values: SupplementFormValues,
): Partial<Record<keyof SupplementFormValues, string>> => {
  const errors: Partial<Record<keyof SupplementFormValues, string>> = {};

  if (!values.name.trim()) {
    errors.name = 'Informe o nome do suplemento.';
  }

  if (!values.amount.trim() || Number(values.amount.replace(',', '.')) <= 0) {
    errors.amount = 'Informe a quantidade.';
  }

  if (values.unitId === 'other' && !values.customUnit.trim()) {
    errors.customUnit = 'Informe a unidade.';
  }

  if (!DATE_ONLY_PATTERN.test(values.date)) {
    errors.date = 'Informe uma data válida.';
  }

  if (!/^\d{2}:\d{2}$/.test(values.time.trim())) {
    errors.time = 'Informe o horário.';
  }

  if (values.frequencyId === 'custom' && !values.customFrequency.trim()) {
    errors.customFrequency = 'Descreva a frequência.';
  }

  return errors;
};

export const resolveSupplementUnitLabel = (
  entry: Pick<SupplementEntry, 'unitId' | 'customUnit'>,
): string => {
  if (entry.unitId === 'other') {
    return entry.customUnit?.trim() || SUPPLEMENT_UNIT_LABEL.other;
  }

  return SUPPLEMENT_UNIT_LABEL[entry.unitId];
};

export const resolveSupplementFrequencyLabel = (
  entry: Pick<SupplementEntry, 'frequencyId' | 'customFrequency'>,
): string => {
  if (entry.frequencyId === 'custom') {
    return entry.customFrequency?.trim() || SUPPLEMENT_FREQUENCY_LABEL.custom;
  }

  return SUPPLEMENT_FREQUENCY_LABEL[entry.frequencyId];
};

export const formatSupplementAmount = (
  entry: Pick<SupplementEntry, 'amount' | 'unitId' | 'customUnit'>,
): string => `${entry.amount.trim()} ${resolveSupplementUnitLabel(entry)}`;

export const sortSupplementsChronologically = (
  entries: SupplementEntry[],
): SupplementEntry[] => (
  [...entries].sort((left, right) => {
    const timeCompare = left.time.localeCompare(right.time);
    if (timeCompare !== 0) {
      return timeCompare;
    }

    return left.createdAt.localeCompare(right.createdAt);
  })
);
